import { useRef, useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, X, MapPin, Tag, ArrowRight } from "lucide-react";
import { useReveal } from "../hooks/useReveal";
import { Link } from "react-router-dom";
import img1 from "../new-assets/img1.jpeg";
import img2 from "../new-assets/img2.jpeg";
import img3 from "../new-assets/img3.jpeg";
import img4 from "../new-assets/img4.jpeg";

interface Project {
    title: string;
    location: string;
    address?: string;
    images: string[];
    tag: string;
    description?: string;
}

const projects: Project[] = [
    {
        title: "Commercial Office Rewire",
        location: "CBD, New Orleans",
        images: [
            "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?q=80&w=1200",
            "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?q=80&w=1200",
            "https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=1200"
        ],
        tag: "Commercial",
        description: "Complete electrical overhaul for a 12,000 sq ft office space, including modern LED lighting systems and data cabling."
    },
    {
        title: "Panel Upgrade — Residential",
        location: "Garden District",
        images: [
            "https://images.unsplash.com/photo-1566417110090-6b15a06ec800?q=80&w=1200",
            "https://images.unsplash.com/photo-1621905252507-b35492cc74b4?q=80&w=1200",
            "https://images.unsplash.com/photo-1544724569-5f546fd6f2b5?q=80&w=1200"
        ],
        tag: "Residential",
        description: "Upgraded a legacy 100A panel to a modern 200A system to support new HVAC and kitchen appliances."
    },
    {
        title: "Generator Installation",
        location: "Metairie, LA",
        images: [
            "https://images.unsplash.com/photo-1413882353314-73389f63b6fd?q=80&w=1200",
            "https://images.unsplash.com/photo-1521207418485-99c705420785?q=80&w=1200",
            "https://images.unsplash.com/photo-1581092160562-40aa08e78837?q=80&w=1200"
        ],
        tag: "Generator",
        description: "Whole-home standby generator installation with automatic transfer switch for storm season reliability."
    },
    {
        title: "Levi's Clothing Store",
        location: "Canal Street, New Orleans",
        address: "Levi's, 500 Port of New Orleans Pl, New Orleans, LA 70130",
        images: [img1, img2, img3, img4],
        tag: "Commercial",
        description: "Complete electrical build-out for the Levi's retail store on Canal Street, including dedicated circuits, clean power distribution, display lighting, track lighting, and support for point-of-sale systems. The work was coordinated around the store layout to keep the space clean, safe, and ready for a professional retail opening."
    },
    {
        title: "Restaurant Full Electrical",
        location: "Marigny, New Orleans",
        images: [
            "https://images.unsplash.com/photo-1555963966-b7ae5404b6ed?q=80&w=1200",
            "https://images.unsplash.com/photo-1579783901586-d88db74b4fe1?q=80&w=1200",
            "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?q=80&w=1200"
        ],
        tag: "Commercial",
        description: "Comprehensive electrical build-out for a new restaurant, featuring custom ambient lighting and industrial kitchen power."
    },
    {
        title: "LED Lighting Redesign",
        location: "Mid-City, NOLA",
        images: [
            "https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?q=80&w=1200",
            "https://images.unsplash.com/photo-1513506003901-1e6a229e2d15?q=80&w=1200",
            "https://images.unsplash.com/photo-1508919892451-4e8cdaec5295?q=80&w=1200"
        ],
        tag: "Lighting",
        description: "Retrofitting a commercial warehouse with high-efficiency LED fixtures for cost savings and improved safety."
    },
];

const tagColors: Record<string, string> = {
    Commercial: "bg-[#2557C7] text-white",
    Residential: "bg-slate-700 text-white",
    Generator: "bg-slate-700 text-white",
    "EV Charger": "bg-[#2557C7] text-white",
    Lighting: "bg-slate-700 text-white",
};

export function Projects({ isFullPage = false }: { isFullPage?: boolean }) {
    const scrollRef = useRef<HTMLDivElement>(null);
    const headerRef = useReveal<HTMLDivElement>();
    const [selectedProject, setSelectedProject] = useState<Project | null>(null);
    const [activeImageIdx, setActiveImageIdx] = useState(0);
    const [isLightbox, setIsLightbox] = useState(false);

    // Show all projects in both full page and featured modes
    const displayedProjects = projects;

    // Sub-component for Project Cards
    const ProjectCard = ({ p, isFullPage, onClick }: { p: Project, isFullPage: boolean, onClick: () => void }) => (
        <div
            onClick={onClick}
            className={`group card-hover relative shrink-0 ${isFullPage ? 'w-full' : 'w-72 md:w-80'} rounded-2xl overflow-hidden bg-slate-800 shadow-xl cursor-pointer`}
            style={{ height: "320px" }}
        >
            <img
                src={p.images[0]}
                alt={p.title}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 opacity-70"
            />
            <div className="absolute inset-0 bg-linear-to-t from-slate-950/90 via-slate-900/20 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-5">
                <span className={`text-xs font-black px-2.5 py-1 rounded-full mb-3 inline-block ${tagColors[p.tag] || "bg-slate-700 text-white"}`}>
                    {p.tag}
                </span>
                <h3 className="text-white font-bold text-base leading-tight">{p.title}</h3>
                <p className="text-slate-400 text-xs mt-1">{p.location}</p>
            </div>
        </div>
    );

    // Dynamic scroll padding to precisely align with the responsive container left-boundary
    const [scrollPadding, setScrollPadding] = useState("24px");

    useEffect(() => {
        const updatePadding = () => {
            if (headerRef.current) {
                const rect = headerRef.current.getBoundingClientRect();
                setScrollPadding(`${rect.left}px`);
            }
        };

        // Initial set and attach resize listener
        updatePadding();
        window.addEventListener("resize", updatePadding);
        return () => window.removeEventListener("resize", updatePadding);
    }, []);

    // Lock body scroll when modal is open
    useEffect(() => {
        if (selectedProject) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
            setActiveImageIdx(0);
            setIsLightbox(false);
        }
    }, [selectedProject]);

    // Handle ESC key
    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                if (isLightbox) setIsLightbox(false);
                else setSelectedProject(null);
            }
        };
        window.addEventListener("keydown", handleEsc);
        return () => window.removeEventListener("keydown", handleEsc);
    }, [isLightbox]);

    const scroll = (dir: "left" | "right") => {
        if (!scrollRef.current) return;
        scrollRef.current.scrollBy({ left: dir === "right" ? 340 : -340, behavior: "smooth" });
    };

    return (
        <section id="projects" className={`py-24 bg-slate-950 overflow-hidden ${isFullPage ? 'min-h-screen' : ''}`}>
            <div className="container mx-auto px-6">
                <div ref={headerRef} className="reveal flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
                    <div>
                        <div className="w-10 h-1 bg-[#2557C7] mb-5" />
                        <h2 className="text-4xl font-black text-white mb-2">
                            {isFullPage ? "Our Projects" : "Featured Projects"}
                        </h2>
                        <p className="text-slate-400">
                            {isFullPage
                                ? "Explore our complete gallery of electrical excellence."
                                : "A glimpse into our recent work across Greater New Orleans."}
                        </p>
                    </div>
                    {!isFullPage && (
                        <div className="flex items-center gap-4">
                            <Link
                                to="/projects"
                                className="text-white font-bold text-sm bg-slate-800 hover:bg-[#2557C7] px-6 py-3 rounded-xl transition-all border border-slate-700 hover:border-blue-500"
                            >
                                View All Projects
                            </Link>
                            <div className="hidden sm:flex gap-2">
                                <button
                                    onClick={() => scroll("left")}
                                    className="bg-slate-800 hover:bg-[#2557C7] text-white p-3 rounded-xl transition-colors"
                                    aria-label="Scroll left"
                                >
                                    <ChevronLeft className="h-5 w-5" />
                                </button>
                                <button
                                    onClick={() => scroll("right")}
                                    className="bg-slate-800 hover:bg-[#2557C7] text-white p-3 rounded-xl transition-colors"
                                    aria-label="Scroll right"
                                >
                                    <ChevronRight className="h-5 w-5" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {isFullPage ? (
                <div className="container mx-auto px-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 pb-12">
                    {displayedProjects.map((p) => (
                        <ProjectCard key={p.title} p={p} isFullPage={true} onClick={() => setSelectedProject(p)} />
                    ))}
                </div>
            ) : (
                <div className="w-full">
                    <div
                        ref={scrollRef}
                        className="flex overflow-x-auto scroll-smooth pb-12 no-scrollbar"
                        style={{
                            scrollbarWidth: "none",
                            msOverflowStyle: "none",
                            paddingLeft: scrollPadding,
                            paddingRight: scrollPadding
                        }}
                    >
                        <div className="flex gap-6 shrink-0">
                            {displayedProjects.map((p) => (
                                <ProjectCard key={p.title} p={p} isFullPage={false} onClick={() => setSelectedProject(p)} />
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Modal */}
            {selectedProject && (
                <div
                    className="fixed inset-0 z-100 flex items-center justify-center p-4 md:p-6"
                    onClick={() => setSelectedProject(null)}
                >
                    {/* Backdrop */}
                    <div className="absolute inset-0 bg-slate-950/90 backdrop-blur-md animate-in fade-in duration-300" />

                    {/* Modal Content */}
                    <div
                        className={`relative w-[95vw] h-[95vh] max-w-none bg-white rounded-3xl overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300 flex flex-col md:grid ${isLightbox ? 'md:grid-cols-1' : 'md:grid-cols-5'} transition-all`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={() => setSelectedProject(null)}
                            className="absolute top-6 right-6 z-50 bg-black/40 hover:bg-black/60 text-white p-2.5 rounded-full backdrop-blur-md transition-all scale-100 hover:scale-110 active:scale-95"
                        >
                            <X className="h-5 w-5" />
                        </button>

                        {/* GALLERY VIEWER - Left side (60% width) */}
                        <div className={`flex flex-col bg-slate-950 relative group min-h-0 ${isLightbox ? 'md:col-span-1 h-full' : 'md:col-span-3 h-[40vh] md:h-auto'}`}>
                            <div
                                className="flex-1 min-h-0 w-full relative cursor-zoom-in overflow-hidden flex items-center justify-center bg-slate-950"
                                onClick={() => setIsLightbox(!isLightbox)}
                            >
                                <img
                                    src={selectedProject.images[activeImageIdx]}
                                    alt={selectedProject.title}
                                    className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ${isLightbox ? 'object-contain' : 'hover:scale-105'}`}
                                />
                                {!isLightbox && (
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100 pointer-events-none">
                                        <div className="bg-white/20 backdrop-blur-md px-5 py-2.5 rounded-full text-white text-sm font-black border border-white/30 shadow-2xl">
                                            VIEW FULLSCREEN
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Thumbnails */}
                            <div className="p-4 md:p-6 bg-slate-950 border-t border-white/5 flex gap-4 overflow-x-auto justify-center">
                                {selectedProject.images.map((img, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => setActiveImageIdx(idx)}
                                        className={`relative w-20 h-14 md:w-24 md:h-16 rounded-xl overflow-hidden shrink-0 transition-all duration-300 ${activeImageIdx === idx ? 'ring-2 ring-[#2557C7] ring-offset-4 ring-offset-slate-950 scale-105 shadow-2xl z-10' : 'opacity-40 hover:opacity-100'}`}
                                    >
                                        <img src={img} className="w-full h-full object-cover" alt="" />
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* INFO PANEL - Right side (40% width) */}
                        {!isLightbox && (
                            <div className="md:col-span-2 p-8 lg:p-10 flex flex-col bg-white overflow-y-auto h-full">
                                <div className="mb-6 shrink-0">
                                    <div className="flex items-center gap-2 text-[#2557C7] text-[10px] font-black uppercase tracking-[0.15em] mb-4">
                                        <Tag className="h-3.5 w-3.5" />
                                        {selectedProject.tag}
                                    </div>
                                    <h3 className="text-2xl lg:text-4xl font-black text-slate-900 mb-3 leading-tight">
                                        {selectedProject.title}
                                    </h3>
                                    <div className="flex items-center gap-2 text-slate-400 text-xs font-bold uppercase tracking-wide">
                                        <MapPin className="h-3.5 w-3.5 text-[#2557C7]" />
                                        {selectedProject.location}
                                    </div>
                                </div>

                                <div className="space-y-4 mb-6 flex-1 overflow-y-auto pr-2">
                                    <p className="text-slate-600 leading-relaxed text-sm lg:text-base">
                                        {selectedProject.description}
                                    </p>
                                    <p className="text-slate-400 text-xs leading-relaxed">
                                        Professional electrical solutions delivered with precision and safety across Greater New Orleans.
                                    </p>
                                </div>

                                <div className="pt-6 border-t border-slate-100 mb-6 shrink-0">
                                    <Link
                                        to="/#contact"
                                        onClick={() => setSelectedProject(null)}
                                        className="group w-full inline-flex items-center justify-center gap-2 bg-[#2557C7] hover:bg-blue-700 text-white px-6 py-4 rounded-xl font-black text-base transition-all duration-300 shadow-lg shadow-blue-500/25 hover:-translate-y-0.5 hover:shadow-blue-500/40 active:scale-[0.98]"
                                    >
                                        Get Estimate
                                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                    </Link>
                                </div>

                                {/* Google Map Embed */}
                                <div className="w-full h-40 shrink-0 rounded-2xl overflow-hidden border border-slate-200 relative">
                                    <iframe
                                        title="Project Location"
                                        src={`https://maps.google.com/maps?q=${encodeURIComponent(selectedProject.address || selectedProject.location)}&t=&z=14&ie=UTF8&output=embed`}
                                        className="absolute inset-0 w-full h-full"
                                        style={{ border: 0 }}
                                        allowFullScreen
                                        loading="lazy"
                                        referrerPolicy="no-referrer-when-downgrade"
                                    />
                                </div>

                                <div className="mt-6 flex items-center justify-between text-[10px] font-black text-slate-300 uppercase tracking-widest px-1 shrink-0">
                                    <span>Power One</span>
                                    <div className="h-1.5 w-1.5 rounded-full bg-slate-200" />
                                    <span>since 2008</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
}